import { createFileRoute, Link } from "@tanstack/react-router";
import { cta } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { MediaLinks } from "@/components/media-links";
import { MediaTiles } from "@/components/media-tiles";
import { LogoMarquee } from "@/components/logo-marquee";
import { BookShelf } from "@/components/book-shelf";
import {
  mediaPodcasts,
  mediaArticles,
  mediaUnlinkedNames,
  books,
} from "@/content/site-content";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import {
  abs,
  mediaListSchema,
  booksListSchema,
  SITE_URL,
} from "@/content/seo";

import portret from "@/assets/sylwia-portrait.jpg.asset.json";
import { Reveal, useParallax } from "@/components/reveal";
import { PhotoFigure } from "@/components/photo-figure";

const title = "Sylwia Sitkowska | Media, podcasty i książki";
const description =
  "Poznaj wywiady, podcasty i książki Sylwii Sitkowskiej. Psychologia, przywództwo i zdrowie psychiczne. Sprawdź kontakt dla mediów.";
const image = abs(ogSocial);


export const Route = createFileRoute("/media-i-publikacje")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/media-i-publikacje` },
      { property: "og:image", content: image },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/media-i-publikacje` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            mediaListSchema(mediaPodcasts, "Podcasty i rozmowy wideo"),
            mediaListSchema(mediaArticles, "Artykuły i wypowiedzi prasowe"),
            booksListSchema(books),


            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Strona główna",
                  item: "https://sylwiasitkowska.pl/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Media i publikacje",
                  item: "https://sylwiasitkowska.pl/media-i-publikacje",
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: MediaPage,
});

function MediaPage() {
  const portretRef = useParallax(16);
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="relative overflow-hidden border-b border-steel">
          <div aria-hidden="true" className="glow-coral -left-24 -top-28 h-72 w-72" />
          <div className="container-page relative grid items-center gap-10 section-pad lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <Reveal>
              <p className="eyebrow">Media i publikacje</p>
              <h1 className="serif-display mt-8 max-w-3xl text-[2.4rem] md:text-[3.4rem]">
                Wypowiedzi w mediach i <span className="italic text-coral-text">książki</span>
              </h1>
              <p className="measure mt-8 border-l-2 border-coral pl-5 text-base leading-relaxed text-ink/80 md:text-lg">
                Rozmowy o psychologii, przywództwie, presji i zdrowiu psychicznym w prasie,
                telewizji i podcastach.
              </p>
            </Reveal>
            <PhotoFigure
              src={portret.url}
              alt="Sylwia Sitkowska, psycholog i psychoterapeutka komentująca w mediach"
              frameRef={portretRef}
              imgClassName="aspect-[4/3] w-full object-cover object-[50%_20%]"
              width={1320}
              height={1372}
              priority
            />
          </div>
        </section>

        <section className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <p className="eyebrow">Posłuchaj i obejrzyj</p>
            <h2 className="serif-display mt-8 text-[1.9rem] md:text-[2.4rem]">
              Podcasty i rozmowy wideo
            </h2>
            <div className="mt-10">
              <MediaTiles
                items={mediaPodcasts}
                initial={3}
                moreLabel="Pokaż więcej rozmów i materiałów wideo"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Do czytania</p>
            <h2 className="serif-display mt-8 text-[1.9rem] md:text-[2.4rem]">
              Artykuły i wywiady prasowe
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft">
              Teksty o presji, decyzjach, perfekcjonizmie i relacjach, w prasie i serwisach
              opiniotwórczych.
            </p>
            <div className="mt-10">
              <MediaLinks
                items={mediaArticles}
                initial={3}
                linkLabel="Przeczytaj"
                moreLabel="Pokaż więcej artykułów"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <p className="eyebrow">Książki</p>
            <h2 className="serif-display mt-8 text-[1.9rem] md:text-[2.4rem]">
              Publikacje książkowe
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft">
              Kliknij okładkę, aby przejść do książki w Empiku.
            </p>
            <div className="mt-10">
              <BookShelf items={books} initial={3} moreLabel="Pokaż więcej książek" />
            </div>
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page py-12 md:py-16">
            <LogoMarquee />
          </div>
        </section>

        <section className="grain bg-ink">
          <div className="container-page py-12 text-mist md:py-16">
            <p className="eyebrow-light">Kontakt dla mediów</p>
            <h2 className="serif-display mt-8 max-w-2xl text-[1.9rem] md:text-[2.6rem]">
              Zapraszasz do rozmowy lub komentarza eksperckiego?
            </h2>
            <p className="measure mt-6 text-base leading-relaxed text-mist/80">
              Napisz temat, format i deadline.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                {...cta("wspolpraca-medialna", "domkniecie", "inne")}
                className="btn-base inline-flex border border-mist text-mist hover:bg-mist hover:text-ink"
              >
                Zapytaj o współpracę medialną
              </Link>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks
        exclude={["/media-i-publikacje"]}
        only={["/psychologia-biznesu", "/o-mnie", "/wystapienia"]}
        heading={"Dalsze kroki"}
      />
      <SiteFooter />
    </div>
  );
}
